import React from "react";
import { MapPin, Phone, Mail, Clock, Instagram, Facebook } from "lucide-react";
import { SALON } from "../data/salonData";

export const Contact = () => {
  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-24 sm:py-28 px-5 sm:px-8 lg:px-12 max-w-7xl mx-auto"
    >
      <div className="text-center max-w-2xl mx-auto reveal">
        <span className="label-eyebrow">Visit Us</span>
        <h2 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight text-luxuryBlack">
          Our doors are open in{" "}
          <span className="hero-script">Glen Eden</span>.
        </h2>
        <p className="mt-5 text-luxuryBlack/70">
          Free street parking outside the salon. We&rsquo;d love to welcome you in.
        </p>
      </div>

      <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Map */}
        <div
          data-testid="contact-map"
          className="lg:col-span-7 reveal rounded-[2rem] overflow-hidden shadow-soft border border-beigeNude bg-white"
        >
          <iframe
            title="Kinjal's Beauty & Hair Map"
            src={SALON.mapsEmbed}
            className="w-full h-[460px] border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            allowFullScreen
          />
        </div>

        {/* Info */}
        <div className="lg:col-span-5 reveal">
          <div className="glass-card rounded-3xl p-7 space-y-6">
            <a
              href={SALON.mapsLink}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-address"
              className="flex items-start gap-4 group"
            >
              <div className="w-11 h-11 rounded-full bg-roseGoldDeep flex items-center justify-center group-hover:bg-roseGold transition-colors">
                <MapPin className="w-5 h-5 text-white transition-colors" />
              </div>
              <div>
                <div className="label-eyebrow">Address</div>
                <div className="text-sm font-medium text-luxuryBlack mt-1">
                  {SALON.address}
                </div>
              </div>
            </a>

            <a
              href={`tel:${SALON.phoneTel}`}
              data-testid="contact-phone"
              className="flex items-start gap-4 group"
            >
              <div className="w-11 h-11 rounded-full bg-roseGoldDeep flex items-center justify-center group-hover:bg-roseGold transition-colors">
                <Phone className="w-5 h-5 text-white transition-colors" />
              </div>
              <div>
                <div className="label-eyebrow">Phone</div>
                <div className="text-sm font-medium text-luxuryBlack mt-1">
                  {SALON.phoneDisplay}
                </div>
              </div>
            </a>

            <a
              href={`mailto:${SALON.email}`}
              data-testid="contact-email"
              className="flex items-start gap-4 group"
            >
              <div className="w-11 h-11 rounded-full bg-roseGoldDeep flex items-center justify-center group-hover:bg-roseGold transition-colors">
                <Mail className="w-5 h-5 text-white transition-colors" />
              </div>
              <div>
                <div className="label-eyebrow">Email</div>
                <div className="text-sm font-medium text-luxuryBlack mt-1 break-all">
                  {SALON.email}
                </div>
              </div>
            </a>

            <div className="flex items-start gap-4">
              <div className="w-11 h-11 rounded-full bg-roseGoldDeep flex items-center justify-center">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <div className="label-eyebrow">Opening Hours</div>
                <ul className="mt-2 space-y-1.5">
                  {SALON.hours.map((h) => (
                    <li
                      key={h.day}
                      className="flex items-center justify-between text-sm text-luxuryBlack/80"
                    >
                      <span>{h.day}</span>
                      <span className="font-medium">{h.time}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <a
                href={SALON.socials.instagram}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-instagram"
                className="w-10 h-10 rounded-full bg-roseGoldDeep text-white flex items-center justify-center hover:bg-roseGold transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href={SALON.socials.facebook}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-facebook"
                className="w-10 h-10 rounded-full bg-roseGoldDeep text-white flex items-center justify-center hover:bg-roseGold transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href={SALON.socials.google}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-google"
                className="ml-auto text-xs uppercase tracking-[0.2em] text-roseGold hover:text-luxuryBlack transition-colors"
              >
                View on Google →
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
